import { useState, memo } from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { ROI_CHART_DATA } from '@/src/constants';
import EconomicImpactModal from './EconomicImpactModal';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (active && payload && payload.length > 0 && payload[0]) {
    return (
      <div className="bg-[#111] border border-white/10 p-3 rounded-lg shadow-xl">
        <p className="text-gray-400 text-xs uppercase mb-1">Efficiency</p>
        <p className="text-white font-bold text-lg">{payload[0].value}% increase</p>
      </div>
    );
  }
  return null;
}

function RoiSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <section className="container mx-auto px-6 py-24 relative">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Graph Visual */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-1/2 relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl opacity-20 blur-lg animate-pulse" />
            <div className="bg-[#080808] border border-white/10 rounded-2xl p-8 h-[400px] relative overflow-hidden group">
              {/* Overlay Text */}
              <div className="absolute top-8 left-8 z-10 pointer-events-none">
                <p className="text-gray-500 text-xs font-mono uppercase tracking-widest mb-2">
                  Return on Investment
                </p>
                <h3 className="text-6xl font-bold text-white mb-2 group-hover:text-red-500 transition-colors duration-500">
                  2.3x
                </h3>
                <p className="text-gray-400 text-sm max-w-[200px]">
                  Deliver outsized value fast. Payback in under six months.
                </p>
              </div>

              {/* Grid Lines */}
              <div className="absolute inset-0 grid grid-cols-4 grid-rows-4 pointer-events-none">
                <div className="border-r border-white/5 h-full" />
                <div className="border-r border-white/5 h-full" />
                <div className="border-r border-white/5 h-full" />
                <div className="border-r border-white/5 h-full" />
              </div>

              {/* Chart */}
              <div className="absolute bottom-0 left-0 right-0 h-[60%] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ROI_CHART_DATA}>
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#ef4444"
                      strokeWidth={3}
                      dot={{ r: 6, fill: '#ef4444', strokeWidth: 0, fillOpacity: 0.5 }}
                      activeDot={{ r: 8, fill: '#fff' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Floating Highlight Box */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
                className="absolute top-[20%] right-[10%] bg-[#1a1a1a] border border-white/10 p-4 rounded-lg shadow-2xl z-20 hidden md:block"
              >
                <div className="flex gap-2 items-center mb-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-xs text-gray-300 font-mono">LIVE ANALYSIS</span>
                </div>
                <div className="text-white text-xl font-bold">30% Capacity Increase</div>
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-1/2"
          >
            <div className="h-1 w-20 bg-gradient-to-r from-red-500 to-orange-500 mb-6" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Ahorra tiempo, reduce riesgos y <br />
              <span className="text-gray-500">construye lo que importa.</span>
            </h2>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              Nuestros sistemas de inteligencia no solo automatizan tareas; descubren patrones
              ocultos en tus operaciones diarias. Convierte el ruido en conocimiento estratégico.
            </p>

            <div className="flex flex-col gap-4">
              {[
                { num: '01', title: 'Prioriza Roadmaps', desc: 'Detecta fricción en el producto automáticamente.' },
                { num: '02', title: 'Innovación Continua', desc: 'Genera PRDs y tickets basados en feedback real.' },
              ].map((item) => (
                <div key={item.num} className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white font-bold group-hover:bg-red-500 group-hover:border-red-500 transition-colors duration-300">
                    {item.num}
                  </div>
                  <div>
                    <h4 className="text-white font-semibold group-hover:text-red-400 transition-colors">
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex items-center gap-2 text-white border-b border-white/30 pb-1 hover:border-white hover:text-white transition-all duration-300 hover:tracking-wide active:text-gray-300"
              >
                Descargar estudio de impacto económico
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <EconomicImpactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}

export default memo(RoiSection);
